import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { type ReactElement, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { WBSButton } from '@/common/WBSButton';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useCoordinatorGroupAccess } from '~/hooks/useIsCoordinatorGroup';
import { useIsGroupAdmin } from '~/hooks/useIsGroupAdmin';
import { useLanguageLabels } from '~/hooks/useLanguageLabels';
import { AvatarNameCell } from '~/modules/tables/AvatarNameCell';
import { useWbsTableQuery } from '~/modules/tables/useWbsTableQuery';
import WbsTable, { type Column } from '~/modules/tables/WbsTable';
import { GroupHeaderWrapper, Title } from '~/styles/layoutElements';
import type { FilterField } from '~/styles/tables/filterandSettingsStyles';
import { formatLongDate } from '~/utils/dateFormat';
import { apiLanguageToLocale } from '~/utils/languageFormat';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/group-members/',
)({
  component: RouteComponent,
});

/** Keys this table's remembered filters and column visibility. */
const TABLE_KEY = 'group-members';

type GroupMemberItem = components['schemas']['GroupMemberItem'];

/**
 * `Column<T>` is a mapped type over `keyof T` (`WbsTable.tsx:65-82`), so a
 * column can only name a key the row actually has — and the API item has no
 * `actions`, because legacy's Actions cell is built from the *viewer's* own id
 * (`Actions/Users/SendMessage.php:16-18`) and carries no server payload. One
 * synthetic key, the shape `student-board.index.tsx` uses for the only other
 * server-mode table with an Actions column. Intersecting the generated type
 * rather than mirroring it by hand is deliberate: `tsc` still fails loudly if
 * the payload renames or drops a field.
 */
type RosterRow = GroupMemberItem & { actions: null };

const toRosterRow = (item: GroupMemberItem): RosterRow => ({
  ...item,
  actions: null,
});

/**
 * A text box hands back a string; the endpoint wants an int and 422s on junk.
 *
 * KNOWN WART, recorded rather than fixed here: non-numeric input (`12abc`, a
 * leading `#`) is dropped silently, so the panel keeps showing the typed value
 * while the roster stays unfiltered — indistinguishable from "everyone
 * matched". The honest fix is a numeric filter field, and there is no such
 * `FilterFieldType`: `styles/tables/filterandSettingsStyles.ts:73-78` declares
 * `text | select | multiselect | date | checkbox` and `TableFilterPanel.tsx:165`
 * switches on it — both shared across 32 table consumers, which W2.3 §6.1/§6.4
 * place outside this row. The identical silent drop ships today for the sibling
 * `student_id` filter (`coordinator/StudentsTable.tsx:47-51`'s `toStudentId`),
 * which §6.1 named as the shape to copy, so fixing it here alone would diverge
 * from the sibling and still leave the framework wart. Fix it once, in the
 * panel, for every table.
 */
const toMemberId = (value: unknown): number | undefined => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

function RouteComponent(): ReactElement | null {
  const { t } = useTranslation();
  const { groupName } = Route.useParams();
  const navigate = useNavigate();
  const { me } = useCurrentUser();
  const memberId = String(me?.id ?? '');
  const languageLabels = useLanguageLabels();

  // Group TYPE only, mirroring `StudentsLayout` (`$groupName.students.tsx:39-57`)
  // and the sidebar link that offers this route. Legacy registers the tab on
  // `{congregation, custom-study-group}` (`custom-group-members-tab.php:5-12`)
  // while this shell also serves the 84 untyped real groups, whose roster is
  // empty by construction under D21. `isResolved` is not optional: a
  // bare `!isCoordinatorGroup` bounces every caller — the group's own
  // coordinator included — before `getGroupBySlug` lands. W2.3 D16.
  const { isCoordinatorGroup, isResolved } = useCoordinatorGroupAccess();
  const refused = isResolved && !isCoordinatorGroup;
  // The VIEWER's role on this group, for the two drill-in links below. This tab
  // admits a mod (its gate is group TYPE), while the Students tab it links into
  // is admin-only (`$groupName.students.tsx:39-57`), so a link offered to a mod
  // would point at a route that bounces.
  const isGroupAdmin = useIsGroupAdmin();

  useEffect(() => {
    if (refused) {
      navigate({
        to: '/groups/$groupName/',
        params: { groupName },
        replace: true,
      });
    }
  }, [refused, groupName, navigate]);

  const mapFilters = useCallback(
    (filters: Record<string, unknown>): Record<string, unknown> => ({
      ...filters,
      member_id: toMemberId(filters.member_id),
    }),
    [],
  );

  const table = useWbsTableQuery({ tableKey: TABLE_KEY, mapFilters });

  const { data: groupData } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  const groupId = groupData?.id ?? 0;

  const {
    data: memberData,
    isFetching,
    refetch,
  } = api.getGroupStudyHelpers.useQuery(
    { path: { group_id: groupId }, query: table.params },
    { enabled: groupId > 0 },
  );

  const members = useMemo(
    () => (memberData?.data ?? []).map(toRosterRow),
    [memberData],
  );

  // Declared client-side, not read from `GET /v3/lists/{key}/filters`: there is
  // no `congregation-members` list key and there is deliberately not going to be
  // one (W2.3 D6 — registering it forces a `@datatable(...)` twin, which
  // publishes a new table on the frozen fullapp datatables surface that no
  // mobile client names). Legacy's two filters are `Members.php:19-20`, and it
  // relabels the shared "Student Name" to plain "Name" for this table, so this
  // is `wbs.name` rather than `lessons.student-name`. The keys are the
  // endpoint's own query params — `useWbsTableQuery` sends panel keys verbatim.
  const filterFields: FilterField[] = useMemo(
    () => [
      {
        key: 'display_name',
        label: t('wbs.name'),
        type: 'text',
      },
      {
        key: 'member_id',
        label: t('profile.user-id'),
        type: 'text',
      },
    ],
    [t],
  );

  // `sortable` tracks the endpoint's four declared sort keys and nothing else:
  // legacy advertises can-order on 7 headers but `group_role`, `student_adopted`
  // and `students_waiting` emit no ORDER BY at all (W2.3 D7). The spelling is
  // load-bearing and nothing catches it at runtime — `WbsTable.tsx:427-431`
  // sends the column key as `sort` and `patterns/lists/dependencies.py:106-108`
  // silently ignores an unrecognised one, so a typo is a sort icon that paints
  // and does nothing. No `sortFn`: in server mode the table never sorts locally.
  const columns: Column<RosterRow>[] = [
    {
      key: 'id',
      title: t('wbs.id'),
      sortable: true,
    },
    {
      key: 'display_name',
      title: t('wbs.name'),
      sortable: true,
      // `linkUserId` is the whole cell, not polish: legacy's Name cell wraps
      // both avatar and name in `<a href="bp_core_get_user_domain($user_id)">`
      // (`Datatable/Abstracts/Base/Column.php:80-90`, `DisplayName.php:31-34`),
      // and `AvatarNameCell` silently degrades to a `<span>` without it.
      render: (_, member) => (
        <AvatarNameCell
          avatar={member.avatar}
          name={member.display_name}
          linkUserId={member.id}
        />
      ),
      minWidth: '40px',
    },
    {
      key: 'role',
      title: t('groups.group-role'),
      // Legacy's labels for both roster group types (`GroupRole.php:16-30`):
      // admin → Coordinator, mod → Study Helper, neither → its literal
      // `__('n/a','wbs')`.
      //
      // Resolved from web's i18n rather than from data, and that is W2.3 D11
      // rather than a shortcut: legacy's labels are per-group-TYPE and
      // per-LOCALE WordPress postmeta (`_bp_group_type_role_labels` on the
      // bp-group-type posts) that NO migrator touches — grep over
      // `digital-api/src/reboot_squared/migration/` returns 0 hits — so v3 has
      // no data source for them and one pair has to serve both types. For
      // CONGREGATION the two existing keys reproduce legacy exactly in all four
      // languages (8 of 8 cells). For CUSTOM_STUDY_GROUP they diverge and the
      // divergence is an improvement: legacy's es falls back to BuddyBoss's
      // untranslated `Organizer`, its fr renders `Organisateur`, and its pt-BR
      // mod label carries a typo (`Auxiliar do Estudos` against the
      // congregation's `de`).
      //
      // The third branch fires on 0 of the 85 migrated
      // memberships and on EVERY row this screen's own POST creates — the only
      // caller hardcodes `role: 'member'`
      // (`admin/.../AddGroupStudyHelperDialog.tsx:41`) and the roster serves
      // member rows legacy's `admins_mods` filter would hide (W2.3 D18). It is
      // reachable; do not delete it.
      render: (role) =>
        role === 'admin'
          ? t('wbs.coordinator')
          : role === 'mod'
            ? t('lessons.study-helper')
            : t('wbs.n-a-2'),
    },
    {
      key: 'students_adopted',
      title: t('lessons.students-adopted'),
      width: '110px',
      // L23, built in W3.9: legacy renders both counts as a drill-in into the
      // students lists, filtered to this helper
      // (`Columns/Users/StudentAdopted.php:27-32`,
      // `Columns/Users/StudentWaiting.php:35-40`) — and only for a group ADMIN,
      // which is `groups_is_user_admin(bp_loggedin_user_id(), $group_id)`, the
      // VIEWER's role and not the row's. A non-admin keeps the bare number,
      // legacy's own `else` branch.
      //
      // The parameter it needs now exists (`study_helper_id` on
      // `get_group_students`) and both destinations render the control. Legacy
      // carries it in the URL fragment; a search param is the same behaviour in
      // the shape the router validates (W3.9 X28).
      render: (value, member) => (
        <RedValue>
          {isGroupAdmin ? (
            <Link
              to="/groups/$groupName/students/all-students/"
              params={{ groupName }}
              search={{ study_helper_id: member.id }}
            >
              {value}
            </Link>
          ) : (
            value
          )}
        </RedValue>
      ),
    },
    {
      key: 'students_waiting',
      title: t('lessons.students-waiting'),
      width: '110px',
      // The destination's total will not equal this number, and that mismatch
      // is legacy's own: this count is grading-row-scoped with no introduction
      // arm (`StudentWaiting.php:21-33`), while the board counts STUDENTS over
      // five categories. Reconciling the two belongs to whoever owns this
      // column, not to the link (W3.9 N6).
      render: (value, member) => (
        <RedValue>
          {isGroupAdmin ? (
            <Link
              to="/groups/$groupName/students/students-waiting/"
              params={{ groupName }}
              search={{ study_helper_id: member.id }}
            >
              {value}
            </Link>
          ) : (
            value
          )}
        </RedValue>
      ),
    },
    {
      key: 'gender',
      title: t('wbs.gender'),
      // FOUR branches. The first three are the house idiom
      // (`coordinator/StudentsTable.tsx:147-156`); the fourth is legacy's own
      // `wbs_gender_display($value) ?: __('n/a','wbs')` (`Gender.php:20`,
      // `Core/functions.php:409-416`), a distinct cell from "Prefer Not to
      // Answer", and it is why the payload types the field nullable (W2.3 D17).
      // It fires on 0 of 904 migrated users today — the null is latent, not
      // dead, so do not collapse it into the `unknown` arm.
      render: (gender) =>
        gender === 'male'
          ? t('wbs.male')
          : gender === 'female'
            ? t('wbs.female')
            : gender === 'unknown'
              ? t('wbs.prefer-not-to-answer')
              : t('wbs.n-a-2'),
    },
    {
      key: 'location',
      title: t('wbs.location'),
      render: (_, member) => (
        <>
          {member.location}
          <br />
          {member.country}
        </>
      ),
    },
    {
      key: 'languages',
      // Plural, and translated 4/4 — legacy renders this header untranslated
      // (its catalogue has no `Languages` unit), so this is free improvement.
      // The column is the teach-students-who-speak list, NOT the account's UI
      // language, which the payload no longer carries (W2.3 L8).
      title: t('wbs.languages'),
      render: (languages) =>
        languages
          .map(
            (language) =>
              languageLabels[apiLanguageToLocale(language)] ?? language,
          )
          .join(', '),
    },
    {
      key: 'last_active',
      title: t('wbs.last-active'),
      sortable: true,
      // `''` for the 4 roster helpers whose legacy `last_active` was
      // `0000-00-00 00:00:00`; `formatLongDate` renders those as an em-dash.
      render: (value) => formatLongDate(value),
    },
    {
      key: 'registered',
      title: t('wbs.registered'),
      sortable: true,
      minWidth: '130px',
      render: (value) => formatLongDate(value),
    },
    {
      key: 'actions',
      title: t('wbs.actions'),
      sortable: false,
      render: (_, member) => {
        return (
          <WBSButton
            onClick={() =>
              // `memberId` is the VIEWER's id and `r` is the row's: legacy builds
              // the href off `bp_core_get_user_domain(bp_loggedin_user_id())`
              // and appends `?r=<row user id>` (`SendMessage.php:16-18`).
              navigate({
                to: '/members/$memberId/messages/compose/',
                params: { memberId },
                search: { r: Number(member.id) },
              })
            }
          >
            {t('wbs.message')}
          </WBSButton>
        );
      },
    },
  ];

  if (refused) return null;

  return (
    <div>
      <GroupHeaderWrapper>
        <Title>{t('profile.members')}</Title>
      </GroupHeaderWrapper>
      {/*
        The empty state is `WbsTable`'s own translated `wbs.no-results-found-please`
        (`WbsTable.tsx:526-527`), and that is W2.3 D12: legacy answers an empty
        roster with `{"success":false,"data":{"error":"no_results"}}` at HTTP
        200, and does the same for a page past the end and for a bogus group id.
        v3 returns 200 with `data: []` — the house envelope, which
        `PaginatedResponse` gives no error arm. In THIS build it is reached only
        through a page past the end or a filter that matches nothing: a roster
        that is empty because the group has no members is not a legacy-parity
        example. The two zero-membership CONGREGATIONs (484, 602) render one row
        in legacy and none here for a different reason — each has exactly one
        legacy admin/mod and it is one of the four `administrator`-profile
        leaders D21 drops (§4.2, §10 item 18) — and no study helper can gain a
        membership there, so the only actor who reaches those pages at all is a
        platform admin through `Requirement`'s `admin_bypass`.
      */}
      <WbsTable
        {...table.tableProps}
        data={members}
        columns={columns}
        totalCount={memberData?.count}
        loading={isFetching}
        onRefresh={() => {
          void refetch();
        }}
        filterFields={filterFields}
        searchPlaceholder={t('wbs.search-by-name-or')}
        showSearch
        showFilter
        showSettings
        showRefresh
        cellStyle={{ padding: '14px 8px' }}
        // Legacy's `data-default-toggles`: gender and location are the only two
        // columns off by default (W2.3 L2).
        //
        // L2's other half — "Actions has no toggle at all"
        // (`Members.php:31-36` gives it no toggle entry) — is NOT reproduced,
        // and the divergence is FRAMEWORK-LEVEL rather than this screen's:
        // `WbsTable.tsx:612` hands the whole `columns` array to
        // `ColumnSettingsPanel`, which renders a visibility toggle for every
        // key, `actions` included, for all 32 of its consumers. Suppressing it
        // means an opt-out on `Column<T>` in `WbsTable.tsx`, which W2.3 §6.4
        // places outside this row (consumed unchanged, not modified). Recorded
        // here so the next reader sees a decision rather than an oversight.
        hiddenColumns={['gender', 'location']}
      />
    </div>
  );
}

const RedValue = styled.span`
  color: var(--wbs-red);
`;
